import { Box, Theme, Tooltip, Typography } from "@material-ui/core";
import { format, parseISO } from "date-fns";
import React from "react";
import {
  Datagrid,
  Filter,
  FunctionField,
  Record,
  SelectInput,
  TextField,
  TextInput,
  useListContext,
  Responsive,
  SimpleList,
  useTranslate,
} from "react-admin";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { Void } from "../../common/data-grid-fields/Void";
import "../../common/styles/list-style.css";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import malfunctionsDiag from ".";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { AcknowledgeDtcMenuItem } from "./AcknowledgeDtcMenuItem";
import { DiagnosticRecordListDto } from "../../../common/data/service-proxies";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { useTheme } from "@material-ui/styles";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import EventIcon from "@material-ui/icons/Event";

const ListFilter = (props: any) => {
  useSearchFields(props, [
    "vehicleName",
    "dtcSourceName",
    "spnCode",
    "fmiCode",
  ]);
  useResetListSearchField(props);
  const { loading } = useListContext();
  return (
    <Filter {...props}>
      {!loading && (
        <TextInput
          className="malfunctions-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      {loading && (
        <TextInput
          className="malfunctions-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        className="list-view-filter"
        label="resources.malfunctionsDiag.labels.dateFrom"
        source="dateFrom"
        alwaysOn
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        className="list-view-filter"
        label="resources.malfunctionsDiag.labels.dateTo"
        source="dateTo"
        alwaysOn
      />
      <SelectInput
        className="list-view-filter"
        label="resources.malfunctionsDiag.labels.acknowledged"
        allowEmpty
        emptyText="resources.malfunctionsDiag.labels.both"
        source="acknowledged"
        alwaysOn
        choices={[
          { id: true, name: "resources.malfunctionsDiag.labels.acknowledged" },
          {
            id: false,
            name: "resources.malfunctionsDiag.labels.notAcknowledged",
          },
        ]}
      />
    </Filter>
  );
};

export const MalfunctionsDiagList = (props: any) => {
  const theme: Theme = useTheme();
  const permissions = useActionPermissions();
  const translate = useTranslate();

  interface SimpleListProps {
    record?: Record;
    isAllowed?: Boolean;
  }

  const RenderPrimaryTextForSimpleList = ({
    record,
    isAllowed,
  }: SimpleListProps) => {
    return (
      <div
        style={{
          width: `${isAllowed && !record?.acknowledged ? "80%" : "100%"}`,
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Tooltip
            title={translate("resources.malfunctionsDiag.labels.vehicleName")}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "6px",
                alignItems: "center",
              }}
            >
              <LocalShippingOutlinedIcon
                style={{
                  color: theme.palette.primary.main,
                }}
              />
              <TextField
                source="vehicleName"
                label="resources.malfunctionsDiag.labels.vehicleName"
              />
            </div>
          </Tooltip>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <EventIcon
              style={{
                color: theme.palette.primary.main,
              }}
            />
            <FunctionField<DiagnosticRecordListDto & Record>
              source="time"
              label="resources.malfunctionsDiag.labels.time"
              render={(record) => {
                return (
                  record?.time && format(parseISO(record.time), "MMM do, yyyy")
                );
              }}
            />
          </div>
          <FunctionField<DiagnosticRecordListDto & Record>
            source="acknowledged"
            headerClassName="centered-content"
            label="resources.malfunctionsDiag.labels.acknowledged"
            render={(record) => (
              <Box className="centered-content">
                {record?.acknowledged ? (
                  <CheckBoxIcon
                    style={{
                      color: theme.palette.success.main,
                    }}
                  />
                ) : (
                  <IndeterminateCheckBoxIcon
                    style={{
                      color: theme.palette.error.main,
                    }}
                  />
                )}
              </Box>
            )}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Code:
          </Typography>
          <TextField source="code" label="Code" />
        </div>
      </div>
    );
  };

  return (
    <div className="content-container">
      <DefaultProLogsList
        title="resources.malfunctionsDiag.name"
        actions={<Void />}
        bulkActionButtons={false}
        filters={<ListFilter />}
        empty={<ProLogsEmpty iconType={malfunctionsDiag.icon} />}
        sort={{ field: "time", order: "desc" }}
        {...props}
      >
        <Responsive
          small={
            <SimpleList
              className="simple-list-wrapper"
              linkType={false}
              primaryText={(record) => (
                <RenderPrimaryTextForSimpleList
                  record={record}
                  isAllowed={
                    permissions[permissionNames.assets.diagnostics.acknowledge]
                  }
                />
              )}
              rightIcon={(record) => {
                return (
                  <>
                    <ActionsField
                      {...props}
                      record={record}
                      editable={() => false}
                    >
                      {permissions[
                        permissionNames.assets.diagnostics.acknowledge
                      ] ? (
                        <AcknowledgeDtcMenuItem
                          hidden={(record) => !!record?.acknowledged}
                        />
                      ) : null}
                    </ActionsField>
                  </>
                );
              }}
            />
          }
          medium={
            <Datagrid>
              <FunctionField<DiagnosticRecordListDto & Record>
                source="time"
                label="resources.malfunctionsDiag.labels.time"
                render={(record) => {
                  return (
                    record?.time &&
                    format(parseISO(record.time), "MMM do, yyyy")
                  );
                }}
              />
              <TextField
                source="vehicleName"
                label="resources.malfunctionsDiag.labels.vehicleName"
              />
              <TextField source="code" label="Code" />
              <FunctionField<DiagnosticRecordListDto & Record>
                source="acknowledged"
                headerClassName="centered-content"
                label="resources.malfunctionsDiag.labels.acknowledged"
                render={(record) => (
                  <Box className="centered-content">
                    {record?.acknowledged ? (
                      <CheckBoxIcon
                        style={{
                          color: theme.palette.success.main,
                        }}
                      />
                    ) : (
                      <IndeterminateCheckBoxIcon
                        style={{
                          color: theme.palette.error.main,
                        }}
                      />
                    )}
                  </Box>
                )}
              />
              <ActionsField editable={() => false}>
                {permissions[permissionNames.assets.diagnostics.acknowledge] ? (
                  <AcknowledgeDtcMenuItem
                    hidden={(record) => !!record?.acknowledged}
                  />
                ) : null}
              </ActionsField>
            </Datagrid>
          }
        />
      </DefaultProLogsList>
    </div>
  );
};
